import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    X, ArrowsClockwise, Bell, WarningCircle, FileText, CheckCircle, Envelope,
} from "@phosphor-icons/react";

/**
 * BulkStaleCvNudgeModal — V1.5.4
 *
 * Two-step flow:
 *   1. Preview:  GET /candidates/stale-cv/preview?days=X&include_missing=Y
 *      → recruiter picks the cutoff + reviews the list of candidates
 *   2. Send:     POST /candidates/stale-cv/nudge  { candidate_ids, preface }
 *      → server emails each eligible candidate (7-day cool-off honoured)
 *
 * Deselect: candidate emails skipped in this batch. Cool-off dedupes
 * candidates who already got the reminder this week.
 */

const CUTOFFS = [
    { days: 90,  label: "3+ months", pill: "amber" },
    { days: 180, label: "6+ months", pill: "red" },
    { days: 365, label: "1+ year",   pill: "red" },
];

function daysBetween(iso) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor((Date.now() - t) / (24 * 3600 * 1000));
}

export default function BulkStaleCvNudgeModal({ open, onClose }) {
    const [days, setDays] = useState(90);
    const [includeMissing, setIncludeMissing] = useState(true);
    const [preface, setPreface] = useState("");
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    const loadPreview = useCallback(async () => {
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.get(
                `/candidates/stale-cv/preview?days=${days}&include_missing=${includeMissing}&limit=500`,
            );
            setItems(data.items || []);
            setSelected(new Set((data.items || []).map((it) => it.id)));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally {
            setLoading(false);
        }
    }, [days, includeMissing]);

    useEffect(() => { if (open) loadPreview(); }, [open, loadPreview]);

    const toggle = (id) => {
        setSelected((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const toggleAll = () => {
        setSelected((s) => (s.size === items.length ? new Set() : new Set(items.map((i) => i.id))));
    };

    const handleSend = async () => {
        if (selected.size === 0) return;
        if (!window.confirm(`Send "Refresh your CV" email to ${selected.size} candidate${selected.size === 1 ? "" : "s"}?`)) return;
        setSending(true);
        try {
            const { data } = await api.post("/candidates/stale-cv/nudge", {
                candidate_ids: Array.from(selected),
                preface: preface.trim(),
            });
            setResult(data);
            const cooled = data.cooled_off || 0;
            const msg = `Sent ${data.sent} · Failed ${data.failed}${cooled > 0 ? ` · Skipped ${cooled} (7-day cool-off)` : ""}`;
            if (data.failed === 0) toast.success(msg);
            else toast.warning(msg);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="bulk-stale-cv-modal"
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-3xl max-h-[90vh] rounded-lg shadow-2xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <header className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                            <Bell size={10} weight="fill" className="inline mr-1" />
                            V1.5.4 — Bulk CV nudge
                        </p>
                        <h3 className="font-heading text-2xl font-black text-ink tracking-tight">
                            Refresh stale CVs
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Send a personalised "Refresh your CV" email to candidates whose CV is older than the cutoff, or who have no CV on file.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="bulk-stale-close"
                        className="text-slate-400 hover:text-slate-700 p-1"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </header>

                {/* Filters */}
                <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3 bg-slate-50">
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Cutoff</span>
                    {CUTOFFS.map((c) => (
                        <button
                            key={c.days}
                            data-testid={`stale-cutoff-${c.days}`}
                            onClick={() => setDays(c.days)}
                            className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 border transition-colors ${
                                days === c.days
                                    ? "bg-cobalt text-white border-cobalt"
                                    : "bg-white text-slate-600 border-slate-300 hover:border-cobalt"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 ml-2">
                        <input
                            type="checkbox"
                            checked={includeMissing}
                            onChange={(e) => setIncludeMissing(e.target.checked)}
                            data-testid="stale-include-missing"
                        />
                        <span>Include candidates with no CV</span>
                    </label>
                </div>

                {/* Optional preface */}
                <div className="px-5 py-3 border-b border-slate-100">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">
                        Optional preface (goes in every email)
                    </label>
                    <input
                        type="text"
                        data-testid="stale-preface"
                        placeholder="e.g. We have 5 new senior backend roles matching your profile"
                        maxLength={400}
                        value={preface}
                        onChange={(e) => setPreface(e.target.value)}
                        className="w-full text-sm border border-slate-200 px-3 py-2 focus:outline-none focus:border-cobalt"
                    />
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-8 text-center text-slate-400 text-sm">
                            <ArrowsClockwise className="animate-spin mx-auto mb-2" size={20} />
                            Loading eligible candidates…
                        </div>
                    ) : items.length === 0 ? (
                        <div className="p-10 text-center text-slate-500">
                            <CheckCircle size={28} weight="fill" className="text-emerald-500 mx-auto mb-2" />
                            <p className="text-sm font-bold">All caught up!</p>
                            <p className="text-xs mt-1">No candidates match this cutoff — everyone's CV is fresh.</p>
                        </div>
                    ) : (
                        <>
                            <div className="px-5 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between sticky top-0">
                                <label className="inline-flex items-center gap-2 text-[11px] font-bold text-slate-600 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selected.size === items.length && items.length > 0}
                                        onChange={toggleAll}
                                        data-testid="stale-select-all"
                                    />
                                    <span>
                                        {selected.size} of {items.length} selected
                                    </span>
                                </label>
                                <span className="text-[10px] text-slate-400 uppercase tracking-wider">
                                    Sorted by recency
                                </span>
                            </div>
                            <ul className="divide-y divide-slate-100" data-testid="stale-list">
                                {items.map((it) => {
                                    const isSel = selected.has(it.id);
                                    const missing = it.cv_status === "missing";
                                    const dsi = daysBetween(it.resume_updated_at);
                                    return (
                                        <li
                                            key={it.id}
                                            data-testid={`stale-item-${it.id}`}
                                            className={`px-5 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-slate-50 ${
                                                isSel ? "" : "opacity-50"
                                            }`}
                                            onClick={() => toggle(it.id)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSel}
                                                readOnly
                                                className="pointer-events-none"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-bold text-ink truncate flex items-center gap-2">
                                                    {it.name || "—"}
                                                    {missing ? (
                                                        <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5">
                                                            <WarningCircle size={9} weight="fill" /> No CV
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5">
                                                            <FileText size={9} weight="fill" /> {dsi != null ? `${dsi}d old` : "stale"}
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-[11px] text-slate-500 truncate">
                                                    <Envelope size={10} weight="fill" className="inline mr-1" />
                                                    {it.email}
                                                    {it.current_title && ` · ${it.current_title}`}
                                                </p>
                                                {it.last_stale_nudge_at && (
                                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                                        Last nudged {new Date(it.last_stale_nudge_at).toLocaleDateString()} — 7-day cool-off may apply.
                                                    </p>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>

                {/* Footer / send */}
                <footer className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
                    {result ? (
                        <div className="text-xs text-slate-600" data-testid="stale-result">
                            <b className="text-emerald-700">{result.sent}</b> sent ·&nbsp;
                            {result.failed > 0 && <span className="text-red-600"><b>{result.failed}</b> failed · </span>}
                            <b>{result.cooled_off}</b> skipped (cool-off)
                        </div>
                    ) : (
                        <div className="text-xs text-slate-500">
                            Emails are throttled + de-duped by a 7-day cool-off per candidate.
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            data-testid="stale-cancel-btn"
                            className="text-[10px] font-black uppercase tracking-wider text-slate-600 hover:text-ink px-3 py-2"
                        >
                            Close
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={sending || selected.size === 0 || loading}
                            data-testid="stale-send-btn"
                            className="bg-cobalt text-white text-[10px] font-black uppercase tracking-wider px-4 py-2 hover:bg-cobalt/90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                        >
                            {sending && <ArrowsClockwise className="animate-spin" size={11} />}
                            {sending ? "Sending…" : `Send ${selected.size} email${selected.size === 1 ? "" : "s"}`}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}
